import { cn } from '@gettactic/helpers';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check } from 'lucide-react';
import * as React from 'react';
import './office-select-item.css';
import { LabelWithHighlight } from '../../../data-display/labels/LabelWithHighlight/LabelWithHighlight';
import { SelectOfficeItemProps } from './SelectOfficeItem';

import { OfficeIcon } from '../../../icons/TacticIcons/OfficeIcon';
import { FolderIcon } from 'lucide-react';
import { TacticIconType } from '../../../icons/TacticIcons/tacticIcons.types';
import { HomeIcon } from '../../../icons/TacticIcons/HomeIcon';
import { OOOIcon } from '../../../icons/TacticIcons/OOOIcon';
import { LocationTypes, LocationVariants } from './utils';
import { OccupancyIndicator } from '../../../data-display/OccupancyIndicator/OccupancyIndicator';

const IconByType: Record<LocationTypes, TacticIconType> = {
  [LocationVariants.office]: OfficeIcon,
  [LocationVariants.dir]: FolderIcon,
  [LocationVariants.OOO]: OOOIcon,
  [LocationVariants.remote]: HomeIcon
} as const;

const DisplayedIcon = ({ type }: { type: LocationTypes }) => {
  const Icon = IconByType[type];
  return (
    <Icon className="h-4 w-4 text-foreground group-data-[state=checked]:hidden" />
  );
};

export const FlatOfficeItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  Omit<SelectOfficeItemProps, 'data'> & {
    type: LocationTypes;
    label: string;
    locationFilter: string;
    total_desks?: number;
    total_open_desks?: number;
  }
>(({ className, type, children, label, locationFilter, ...props }, ref) => {
  return (
    <SelectPrimitive.Item
      ref={ref}
      className={cn(
        'relative flex w-full cursor-pointer select-none items-center',
        'px-4 bg-background py-1.5 text-sm outline-none',
        'focus:bg-gray-100',
        'transition-colors duration-200',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className
      )}
      {...props}
    >
      <span className="absolute left-4 flex h-3.5 w-3.5 items-center justify-center">
        {/* Show Check icon if selected */}
        <SelectPrimitive.ItemIndicator>
          <Check className="h-4 w-4 text-foreground" />
        </SelectPrimitive.ItemIndicator>

        {/* Show OfficeIcon when not selected */}
        <DisplayedIcon type={type} />
      </span>

      <div className="ml-6 w-full flex flex-row items-center justify-between gap-1">
        <SelectPrimitive.ItemText className="flex-1">
          <LabelWithHighlight
            label={label}
            highlight={locationFilter}
          />
        </SelectPrimitive.ItemText>

        {type === LocationVariants.office && (
          <div>
            <OccupancyIndicator
              totalSpaces={props.total_desks ?? 0}
              occupiedSpaces={props.total_open_desks ?? 0}
            />
          </div>
        )}
      </div>
    </SelectPrimitive.Item>
  );
});
